import { Autocomplete, Grid } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { OrderContext } from '../../../context/OrderContext';
import { useFormContext } from 'react-hook-form';
import { OrderProduct } from '../interfaces';
import { isMeasuredLineType, resolveLineId } from '../utils/lineIdentity';
import { GarmentAttributeCatalog } from '../../../interfaces/Company';
import { getGarmentAttributeCatalogValues } from '../../../utils/garmentAttributeCatalogs';

export const AttributesPickerRow = ({
  garmentAttributeCatalogs,
}: {
  garmentAttributeCatalogs: GarmentAttributeCatalog[];
}) => {
  const { t } = useTranslation(['OrdersPage']);
  const { getValues, setValue } = useFormContext();
  const order = getValues();
  const { lastAddedOrderProduct } = useContext(OrderContext);
  const colors = getGarmentAttributeCatalogValues(garmentAttributeCatalogs, 'color');
  const damages = getGarmentAttributeCatalogValues(garmentAttributeCatalogs, 'damage');
  const stains = getGarmentAttributeCatalogValues(garmentAttributeCatalogs, 'stain');
  const brands = getGarmentAttributeCatalogValues(garmentAttributeCatalogs, 'brand');
  const [currentProductState, setCurrentProductState] = useState<{
    id: string | null;
    quantity: number;
  }>({
    id: null,
    quantity: 0
  });
  const [formValues, setFormValues] = useState({
    color: '',
    damage: '',
    stain: '',
    brand: ''
  });

  useEffect(() => {
    if (!lastAddedOrderProduct) return;

    const targetLineId = resolveLineId(lastAddedOrderProduct);
    const productInOrder = order.products.find(
      (product: OrderProduct) => resolveLineId(product) === targetLineId
    );

    if (!productInOrder || productInOrder.returnSourceOrderLineId) return;

    const isNewProductOrQuantityChange =
      targetLineId !== currentProductState.id ||
      productInOrder.quantity !== currentProductState.quantity;

    if (isNewProductOrQuantityChange) {
      setCurrentProductState({
        id: targetLineId,
        quantity: productInOrder.quantity
      });
      setFormValues({
        color: '',
        damage: '',
        stain: '',
        brand: ''
      });
    }
  }, [order, lastAddedOrderProduct, currentProductState]);

  const handleOnChange = (value: string, field: string) => {
    if (!lastAddedOrderProduct) return;
    setFormValues((prev) => ({ ...prev, [field]: value }));

    const targetLineId = resolveLineId(lastAddedOrderProduct);
    const productFound = order.products.find(
      (product: OrderProduct) => resolveLineId(product) === targetLineId
    );
    if (productFound && !productFound.returnSourceOrderLineId) {
      if (!productFound.attributes) {
        productFound.attributes = [{}];
      }
      if (isMeasuredLineType(productFound)) {
        productFound.attributes = [productFound.attributes?.[0] || {}];
        productFound.attributes[0][field] = value;
      } else {
        const lastAttribute = productFound.attributes[productFound.attributes.length - 1];
        lastAttribute[field] = value;
      }
      setValue('products', order.products);
    }
  };

  return (
    <Grid w={'100%'} p={'md'}>
      <Grid.Col span={3}>
        <Autocomplete
          placeholder={t('OrdersPage:productsModal.colors')}
          data={colors}
          value={formValues.color}
          onChange={(value) => handleOnChange(value, 'color')}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        <Autocomplete
          placeholder={t('OrdersPage:productsModal.damage')}
          data={damages}
          value={formValues.damage}
          onChange={(value) => handleOnChange(value, 'damage')}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        <Autocomplete
          placeholder={t('OrdersPage:productsModal.stain')}
          data={stains}
          value={formValues.stain}
          onChange={(value) => handleOnChange(value, 'stain')}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        <Autocomplete
          placeholder={t('OrdersPage:productsModal.brand')}
          data={brands}
          value={formValues.brand}
          onChange={(value) => handleOnChange(value, 'brand')}
        />
      </Grid.Col>
    </Grid>
  );
};
